import { useCallback, useEffect, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import {
  disablePush,
  enablePush,
  getPushState,
  type PushState,
} from "../../lib/push";
import { useSession } from "../../session";

import { CloseoutPerson } from "./CloseoutPerson";
import { NoteTagMailbox } from "./NoteTagMailbox";
import { SigningAlertPeople } from "./SigningAlertPeople";
import { InsuranceEmail } from "./InsuranceEmail";
import { ThursdayReminder } from "./ThursdayReminder";

/**
 * Turn notifications on for this device.
 *
 * Deliberately per-device rather than per-person: a subscription belongs to one
 * browser on one machine, so somebody who uses a phone and a laptop turns it on
 * in both.
 */

/** Translated where it is shown; every sentence here is in the dictionary. */
const EXPLANATION: Record<PushState, string> = {
  on: "This device will receive notifications.",
  off: "Notifications are off on this device.",
  denied:
    "This browser has blocked notifications for the app. Turn them back on in the browser’s site settings, then reload.",
  unsupported: "This browser cannot receive web notifications.",
  "needs-install":
    "On iPhone and iPad, notifications only work once the app is installed. Tap Share, then “Add to Home Screen”, and open it from there.",
  unconfigured:
    "Push is not configured for this deployment — VITE_VAPID_PUBLIC_KEY is not set.",
};

export function NotificationsTool() {
  const { profileId } = useSession();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setState(await getPushState());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function toggle() {
    if (!profileId) {
      setError(t("Your login is not linked to a staff record yet."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setState(
        state === "on" ? await disablePush() : await enablePush(profileId),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (state === null) return <div className="stub">{t("Checking…")}</div>;

  const canToggle = state === "on" || state === "off";

  return (
    <>
      <ErrorNote error={error} />

      <div className="rrow">
        <span className="k">{t("This device")}</span>
        <span className="v">
          <span className={`badge ${state === "on" ? "approved" : "draft"}`}>
            {state === "on" ? t("on") : t("off")}
          </span>
        </span>
      </div>

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 520 }}>
        {t(EXPLANATION[state])}
      </p>

      {canToggle && (
        <button
          className="addrow"
          disabled={busy}
          onClick={() => void toggle()}
        >
          {state === "on"
            ? t("Turn off on this device")
            : t("Turn on notifications")}
        </button>
      )}

      <div className="na-hint" style={{ marginTop: 14, maxWidth: 520 }}>
        {t(
          "Notifications are per device — turn them on separately on your phone and your computer.",
        )}
      </div>

      <SigningAlertPeople />

      <NoteTagMailbox />

      <ThursdayReminder />

      <InsuranceEmail />

      <CloseoutPerson />
    </>
  );
}
